import React from 'react';
import { MediaLibraryItem } from '../../api/media';
import API from '../../api/api';
import { Button } from '../ui';
import { Modal } from '../modals';
import { AssetPoolItem } from './assetPool';
import BrowseTab from './attachment-library/BrowseTab';
import UploadTab from './attachment-library/UploadTab';
import { groupMediaItems } from './attachment-library/helpers';
import {
  AttachmentLibraryMode,
  useAttachmentLibraryState,
} from './attachment-library/useAttachmentLibraryState';

interface AttachmentLibraryModalProps {
  isOpen: boolean;
  mode: AttachmentLibraryMode;
  generatedAssets?: AssetPoolItem[];
  initialSelectedMediaIds?: string[];
  /**
   * Full picked item objects to seed the cross-page selection. Preferred over
   * `initialSelectedMediaIds` for select-mode callers that already hold their
   * picks — the objects survive even if their library page is never re-opened.
   */
  initialSelectedItems?: MediaLibraryItem[];
  onClose: () => void;
  onConfirmSelection?: (items: MediaLibraryItem[]) => void;
}

const AttachmentLibraryModal: React.FC<AttachmentLibraryModalProps> = ({
  isOpen,
  mode,
  generatedAssets = [],
  initialSelectedMediaIds = [],
  initialSelectedItems,
  onClose,
  onConfirmSelection,
}) => {
  const {
    activeItem,
    activeTab,
    contextDraft,
    aiDescription,
    descriptionDraft,
    renameDraft,
    errorMessage,
    filteredItems,
    folderTotals,
    folderType,
    handleRemoveActive,
    handleFileClick,
    handleRenameActive,
    handleSaveContext,
    handleDescribe,
    handleSaveDescription,
    handleUpload,
    handleSearchSubmit,
    handlePageChange,
    loadLibrary,
    fullItems,
    deleting,
    loading,
    renaming,
    savingContext,
    describing,
    savingDescription,
    searchQuery,
    selectedItems,
    selectedIds,
    setActiveTab,
    setContextDraft,
    setDescriptionDraft,
    setFolderType,
    setRenameDraft,
    setSearchQuery,
    setSourceFilter,
    setUploadFiles,
    sourceFilter,
    statusMessage,
    uploadFiles,
    uploading,
    currentPage,
    totalPages,
    totalItems,
    viewMode,
    setViewMode,
    togglingFavouriteId,
    handleToggleFavourite,
    handleToggleSelect,
    handleRangeSelect,
    handleInspect,
    handleClearSelection,
    handleBulkFavourite,
    handleBulkDelete,
    selectedCount,
    pageSize,
    sortKey,
    hasControlnet,
    recentDays,
    activePresetId,
    presets,
    handlePageSizeChange,
    handleSortChange,
    handleHasControlnetChange,
    handleRecentDaysChange,
    handleApplyPreset,
    handleSavePreset,
    handleDeletePreset,
    handleClearFilters,
    handleSelectAllMatching,
    selectingAll,
    allMatchingSelected,
    handleCropSaved,
  } = useAttachmentLibraryState({
    isOpen,
    mode,
    generatedAssets,
    initialSelectedMediaIds,
    initialSelectedItems,
  });

  const [clipNames, setClipNames] = React.useState<Record<string, string>>({});

  React.useEffect(() => {
    if (!isOpen) return;
    // scope 'all': this map names media that may belong to clips archived long
    // ago, so the active window would leave older groups unlabelled.
    API.getClipPrompts('all')
      .then((data) => {
        const clips = data?.clips ?? [];
        const map: Record<string, string> = {};
        for (const c of clips) {
          if (c.id && c.name) map[c.id] = c.name;
        }
        setClipNames(map);
      })
      .catch(() => {});
  }, [isOpen]);

  const groups = React.useMemo(
    () => groupMediaItems(fullItems, clipNames),
    [fullItems, clipNames]
  );

  // Stable callbacks so the memoized BrowseTab rows don't re-render on every
  // unrelated parent state change (search typing, toggling another item, etc).
  const handleFileClickStable = React.useCallback(
    (id: string) => void handleFileClick(id),
    [handleFileClick]
  );
  const handleToggleFavouriteStable = React.useCallback(
    (id: string, current: boolean) => void handleToggleFavourite(id, current),
    [handleToggleFavourite]
  );
  const handleConfirm = React.useCallback(
    () => onConfirmSelection?.(selectedItems),
    [onConfirmSelection, selectedItems]
  );

  const displayedItems = filteredItems;

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title={mode === 'manage' ? 'Media Upload Library' : 'Select Files For Next Run'}
      size="xl"
    >
      {/* Balanced header: the Browse/Upload tab group sits as one segmented
          control on the left; the action group (Refresh) on the right. The split
          is owned by justify-between — no lone `ml-auto` button floating off. */}
      <div className="flex flex-wrap items-center justify-between gap-x-6 gap-y-2">
        <div className="flex items-center gap-2">
          <button
            type="button"
            className={`btn btn-sm ${activeTab === 'browse' ? 'btn-secondary' : 'btn-ghost'}`}
            onClick={() => setActiveTab('browse')}
          >
            Browse Files
          </button>
          <button
            type="button"
            className={`btn btn-sm ${activeTab === 'upload' ? 'btn-secondary' : 'btn-ghost'}`}
            onClick={() => setActiveTab('upload')}
          >
            Upload Files
          </button>
        </div>
        <div className="flex items-center gap-2">
          <Button type="button" variant="ghost" size="sm" onClick={() => void loadLibrary()} disabled={loading}>
            Refresh
          </Button>
        </div>
      </div>

      {activeTab === 'upload' ? (
        <UploadTab
          uploadFiles={uploadFiles}
          uploading={uploading}
          onUploadFilesChange={setUploadFiles}
          onUpload={() => void handleUpload()}
        />
      ) : (
        <BrowseTab
          loading={loading}
          folderType={folderType}
          sourceFilter={sourceFilter}
          searchQuery={searchQuery}
          folderCounts={folderTotals}
          filteredItems={displayedItems}
          selectedIds={selectedIds}
          activeItem={activeItem}
          mode={mode}
          contextDraft={contextDraft}
          aiDescription={aiDescription}
          descriptionDraft={descriptionDraft}
          renameDraft={renameDraft}
          savingContext={savingContext}
          describing={describing}
          savingDescription={savingDescription}
          renaming={renaming}
          deleting={deleting}
          currentPage={currentPage}
          totalPages={totalPages}
          totalItems={totalItems}
          pageSize={pageSize}
          sortKey={sortKey}
          hasControlnet={hasControlnet}
          recentDays={recentDays}
          presets={presets}
          activePresetId={activePresetId}
          viewMode={viewMode}
          groups={groups}
          togglingFavouriteId={togglingFavouriteId}
          errorMessage={errorMessage}
          selectedCount={selectedCount}
          onFolderTypeChange={setFolderType}
          onSourceFilterChange={setSourceFilter}
          onSearchQueryChange={setSearchQuery}
          onSearchSubmit={handleSearchSubmit}
          onPageChange={handlePageChange}
          onPageSizeChange={handlePageSizeChange}
          onSortChange={handleSortChange}
          onHasControlnetChange={handleHasControlnetChange}
          onRecentDaysChange={handleRecentDaysChange}
          onPresetApply={handleApplyPreset}
          onPresetSave={handleSavePreset}
          onPresetDelete={handleDeletePreset}
          onClearFilters={handleClearFilters}
          onFileClick={handleFileClickStable}
          onContextDraftChange={setContextDraft}
          onDescriptionDraftChange={setDescriptionDraft}
          onDescribe={(force) => void handleDescribe(force)}
          onRenameDraftChange={setRenameDraft}
          onSaveContext={() => void handleSaveContext()}
          onSaveDescription={() => void handleSaveDescription()}
          onRename={() => void handleRenameActive()}
          onRemove={() => void handleRemoveActive()}
          onViewModeChange={setViewMode}
          onToggleFavourite={handleToggleFavouriteStable}
          onToggleSelect={handleToggleSelect}
          onInspect={handleInspect}
          onRangeSelect={handleRangeSelect}
          onAdditiveToggle={handleToggleSelect}
          onClearSelection={handleClearSelection}
          onSelectAllMatching={() => void handleSelectAllMatching()}
          selectingAll={selectingAll}
          allMatchingSelected={allMatchingSelected}
          onConfirm={handleConfirm}
          onBulkFavourite={() => void handleBulkFavourite()}
          onBulkDelete={() => void handleBulkDelete()}
          onRetry={() => void loadLibrary()}
          onCropSaved={handleCropSaved}
        />
      )}

      {errorMessage && <p className="text-xs text-rose-300">{errorMessage}</p>}
      {statusMessage && <p className="text-xs text-emerald-300">{statusMessage}</p>}

      {mode === 'select' && (
        <div className="flex items-center justify-end gap-2 border-t border-[var(--border-subtle)] pt-3">
          <Button type="button" variant="ghost" size="sm" onClick={onClose}>
            X Cancel
          </Button>
        </div>
      )}
    </Modal>
  );
};

export default AttachmentLibraryModal;
